/**
 * Transform existing mobile tasks API response to store-first structure
 * The backend returns stores (one per store-campaign combo) with formatTasks, 
 * we transform to stores with campaigns containing packTask
 */
import type { StoreVisit, CampaignAtStore, PackTask } from '@/types/store-first';

export function transformMobileTasksToStoreVisits(stores: any[]): StoreVisit[] {
  console.log('[TRANSFORM] Input stores:', stores?.length || 0, stores);
  
  // Ensure stores is an array
  if (!stores || !Array.isArray(stores) || stores.length === 0) {
    console.log('[TRANSFORM] No stores to transform or invalid input');
    return [];
  }

  // ✅ PRIORITY 1 FIX: Extract operatorId from first task
  const operatorId = stores[0]?.formatTasks?.[0]?.operatorId || stores[0]?.operatorId || null;
  if (!operatorId) {
    console.warn('[TRANSFORM] No operatorId found in stores data');
  }

  // First, group by storeId (the backend returns one entry per store-campaign combo)
  const storesMap = new Map<number, {
    storeId: number;
    storeName: string;
    storeAddress: string;
    cycleId: number;
    cycleName: string;
    campaigns: Map<number, CampaignAtStore>;
  }>();

  for (const storeEntry of stores) {
    const storeId = storeEntry.storeId;
    
    if (!storeId) {
      console.warn('[TRANSFORM] Skipping store entry without storeId:', storeEntry);
      continue;
    }
    
    // Initialize store if not exists
    if (!storesMap.has(storeId)) {
      storesMap.set(storeId, {
        storeId,
        storeName: storeEntry.storeName,
        storeAddress: storeEntry.storeAddress || 'No address provided',
        cycleId: storeEntry.cycleId,
        cycleName: storeEntry.cycleName || `Cycle ${storeEntry.cycleId}`,
        campaigns: new Map()
      });
    }

    const store = storesMap.get(storeId)!;
    
    // Process formatTasks (each store entry has tasks from one campaign)
    const formatTasks = storeEntry.formatTasks || [];
    console.log(`[TRANSFORM] Store ${storeId} has ${formatTasks.length} formatTasks`);
    
    for (const task of formatTasks) {
      const campaignId = task.campaignId;
      if (!campaignId) {
        console.warn('[TRANSFORM] Skipping task without campaignId:', task);
        continue;
      }

      const mediaRequirements = Array.isArray(task.mediaRequirements) ? task.mediaRequirements : [];

      // Create or merge campaign entry
      if (!store.campaigns.has(campaignId)) {
        // mediaRequirements[].quantity already contains the correct planned quantity.
        // Do NOT multiply by targetQuantity — it is the total media count, not a pack multiplier.
        const initialFormats = mediaRequirements.map((req: any) => ({
          formatId: req.formatId,
          formatName: req.formatName || `Format ${req.formatId}`,
          formatType: req.formatType || null,
          plannedQuantity: Number(req.quantity) || 0,
          deliveredQuantity: 0,
        }));

        const realAssignmentId = Number(task.realAssignmentId ?? task.assignmentId ?? task.taskId ?? task.id ?? 1);
        const legacyAssignmentSource = task.legacyAssignmentId ?? task.id ?? task.taskId ?? null;
        const legacyAssignmentId = legacyAssignmentSource != null ? Number(legacyAssignmentSource) : null;

        const packTask: PackTask = {
          assignmentId: realAssignmentId,
          operatorId: operatorId || 0,  // ✅ PRIORITY 1 FIX: Include operatorId
          legacyAssignmentId,
          packId: task.packId || null,
          plannedQuantity: initialFormats.reduce((sum: number, f: any) => sum + (Number(f.plannedQuantity) || 0), 0) || Number(task.targetQuantity) || 0,
          deliveredQuantity: 0,
          status: task.status || 'pending',
          formats: initialFormats,
          notes: null,
          // ✅ PRIORITY 1 FIX: Include posting instructions
          postingInstructionsText: task.postingInstructionsText || null,
          postingInstructionsImageUrl: task.postingInstructionsImageUrl || null,
          // ✅ PRIORITY 1 FIX: Include rejection reason
          rejectionReason: task.rejectionReason || task.rejection_reason || null,
        };

        store.campaigns.set(campaignId, {
          campaignId,
          campaignName: task.campaignName,
          cycleId: store.cycleId,
          cycleName: store.cycleName,
          packId: task.packId || null,
          packName: task.packName || task.campaignName,
          status: task.status || 'pending',
          packTask,
          notes: null,
          imageUrl: task.postingInstructionsImageUrl || null,
        });
      } else {
        // Merge additional media requirements into existing campaign's formats
        const existing = store.campaigns.get(campaignId)!;
        const formatsById = new Map<number, any>();
        for (const f of existing.packTask.formats) {
          formatsById.set(f.formatId, { ...f });
        }

        for (const req of mediaRequirements) {
          const fid = Number(req.formatId);
          const qty = Number(req.quantity) || 0;
          if (formatsById.has(fid)) {
            const prev = formatsById.get(fid)!;
            formatsById.set(fid, {
              ...prev,
              plannedQuantity: (Number(prev.plannedQuantity) || 0) + qty,
            });
          } else {
            formatsById.set(fid, {
              formatId: fid,
              formatName: req.formatName || `Format ${fid}`,
              formatType: req.formatType || null,
              plannedQuantity: qty,
              deliveredQuantity: 0,
            });
          }
        }

        const mergedFormats = Array.from(formatsById.values());
        existing.packTask.formats = mergedFormats;
        existing.packTask.plannedQuantity = mergedFormats.reduce((sum: number, f: any) => sum + (Number(f.plannedQuantity) || 0), 0) || Number(existing.packTask.plannedQuantity) || 0;
        // Keep status/image/notes as-is
        store.campaigns.set(campaignId, existing);
      }
    }
  }

  // Convert to final structure
  const result = Array.from(storesMap.values()).map(store => ({
    operatorId: operatorId || 0,  // ✅ PRIORITY 1 FIX: Include operatorId
    storeId: store.storeId,
    storeName: store.storeName,
    storeAddress: store.storeAddress,
    retailerId: null, // Not in existing response
    retailerName: null, // Not in existing response
    cycleId: store.cycleId,
    cycleName: store.cycleName,
    visitStatus: 'pending' as const, // Default status
    visitStartedAt: null,
    visitCompletedAt: null,
    visitPausedAt: null,
    estimatedDuration: 0,
    actualDuration: null,
    campaigns: Array.from(store.campaigns.values())
  }));
  
  console.log('[TRANSFORM] Result:', result.length, 'stores with', result.reduce((sum, s) => sum + (s.campaigns?.length || 0), 0), 'total campaigns');
  
  return result;
}

